import Image from "next/image"
import Link from "next/link"

import { Button } from "@/components/ui/button"
import type { BannerDTO } from "@/domain/cms/contracts"
import { cn } from "@/lib/utils"

export type BannerCardProps = {
  banner: BannerDTO
  sizes: string
  featured?: boolean
  mobile?: boolean
  priority?: boolean
  /** Eager when this card reuses the hero file. Otherwise the default is lazy. */
  loading?: "eager" | "lazy"
  className?: string
}

export function BannerCard({
  banner,
  sizes,
  featured = false,
  mobile = false,
  priority = false,
  loading = "lazy",
  className
}: BannerCardProps) {
  const imageUrl = mobile
    ? (banner.mobileImageUrl ?? banner.imageUrl)
    : banner.imageUrl
  const eager = priority || loading === "eager"

  return (
    <div
      className={cn(
        "group relative w-full overflow-hidden rounded-lg bg-neutral-900",
        featured && "aspect-37/20 md:aspect-auto",
        className
      )}
    >
      <Image
        src={imageUrl}
        alt={banner.imageAlt}
        sizes={sizes}
        fill
        priority={priority}
        fetchPriority={eager ? "high" : "auto"}
        loading={eager ? "eager" : "lazy"}
        className="rounded-lg object-cover"
        {...(banner.blurDataUrl
          ? { placeholder: "blur" as const, blurDataURL: banner.blurDataUrl }
          : {})}
      />
      {/* The whole card is the link; the CTA below is painted on top of it and
          stays non-interactive so the banner exposes a single target. */}
      <Link
        href={banner.redirectUrl}
        aria-label={banner.linkLabel}
        className="absolute inset-0 z-20"
      />
      {/* Left inset is 20px. The hero keeps 40px above the copy and 40px under
          the button. Compact cards match that from 1080px; below it they use
          30px top and 20px bottom. */}
      <div
        className={cn(
          "absolute z-10",
          featured
            ? "top-10 left-5"
            : "top-7.5 bottom-5 left-5 min-[1080px]:top-10 min-[1080px]:bottom-10"
        )}
      >
        {banner.preTitle && (
          <p className={cn(!featured && "text-[10px]")}>{banner.preTitle}</p>
        )}
        {/* Null or blank title means the artwork carries the headline. */}
        {banner.title && (
          <h2
            className={cn(
              "font-extrabold leading-[90%]",
              featured ? "text-5xl" : "text-2xl"
            )}
          >
            {banner.title}
          </h2>
        )}
        {banner.description && (
          <p className={cn(featured ? "text-xs" : "text-[10px]")}>
            {banner.description}
          </p>
        )}
      </div>
      {/* The card is the link, so the button never receives the pointer.
          The glow follows the card hover instead of the button's own hover. */}
      {featured && banner.ctaText && (
        <Button
          asChild
          aria-hidden
          tabIndex={-1}
          className="pointer-events-none absolute bottom-10 left-5 z-30 h-11 w-[145px] group-hover:shadow-[0_0_7px_0_--theme(--color-purple-200/45%),0_0_14px_1px_--theme(--color-purple-200/22%)]"
          variant="hand"
        >
          <span>{banner.ctaText}</span>
        </Button>
      )}
    </div>
  )
}
